import React, { useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { ResultsLayout } from 'components/layout/ResultsLayout'
import type { Filter } from 'components/SearchAndFilter/SearchAndFilter'
import { useSearchAndFilter } from 'components/SearchAndFilter/useSearchAndFilter'
import { useGetApplicationsQuery } from 'hooks/queries/Customers/useGetApplicationsQuery'
import { useFeatureFlags } from 'hooks/useFeatureFlags'
import { usePaginationWithApi } from 'hooks/usePaginationWithApi'
import type { CustomerCustodyModel } from 'types/customers'
import { ApplicationItems } from '../CustomersListV2/ApplicationItems'
import { CUSTOMER_STATUS_VERIFIED } from '../helpers/constants'
import { custodyModelLabelMap, custodyModelUseCasesMap, customerTypeLabelMap } from '../utils/utils'

interface LiveCustomersFilters {
  [key: string]: string[] | undefined
  types?: string[]
  custodyModels?: string[]
}

const INITIAL_FILTERS: LiveCustomersFilters = {}

interface LiveCustomersListProps {
  onRefetch?: (refetch: () => void) => void
}

export const LiveCustomersList = ({ onRefetch }: LiveCustomersListProps) => {
  const { t } = useTranslation()
  const { enableCustodyModelFilter } = useFeatureFlags()

  const { page, rowsPerPage, handleChangePage, handleChangeRowsPerPage, resetPage } =
    usePaginationWithApi()

  const {
    searchValue,
    appliedSearch,
    appliedFilters,
    hasSearchAndFilter,
    setSearchValue,
    onFilterChange,
    onResetAll,
  } = useSearchAndFilter('', INITIAL_FILTERS)

  const useCases = appliedFilters?.custodyModels?.flatMap(
    custodyModel => custodyModelUseCasesMap[custodyModel as CustomerCustodyModel] ?? []
  )

  const { data, isFetching, isError, refetch } = useGetApplicationsQuery({
    name: appliedSearch || undefined,
    page,
    size: rowsPerPage,
    statuses: [CUSTOMER_STATUS_VERIFIED],
    types: appliedFilters?.types,
    useCases: useCases?.length ? useCases : undefined,
  })

  useEffect(() => {
    onRefetch?.(refetch)
  }, [refetch, onRefetch])

  const applications = data?.content ?? []
  const totalElements = data?.totalElements ?? 0

  return (
    <ResultsLayout
      testId="live-customers-results"
      isLoading={isFetching}
      isError={isError}
      hasResults={!!applications.length}
      hasSearchAndFilter={hasSearchAndFilter}
      searchAndFilter={{
        search: {
          placeholder: t('customers.searchPlaceholder'),
          value: searchValue,
          onChange: value => {
            setSearchValue(value)
            resetPage()
          },
        },
        filters: [
          {
            id: 'types',
            placeholder: t('customers.customerType'),
            values: Object.keys(customerTypeLabelMap),
            checkedValues: appliedFilters?.types ?? [],
            getLabel: (value: string) => customerTypeLabelMap[value],
            onFilterChange: (filterId, option) => {
              onFilterChange(filterId, option)
              resetPage()
            },
          },
          ...(enableCustodyModelFilter
            ? [
                {
                  id: 'custodyModels',
                  placeholder: t('customers.custodyModel'),
                  values: Object.keys(custodyModelLabelMap),
                  checkedValues: appliedFilters?.custodyModels ?? [],
                  getLabel: (value: string) => custodyModelLabelMap[value as CustomerCustodyModel],
                  onFilterChange: (filterId: string, option: string) => {
                    onFilterChange(filterId, option)
                    resetPage()
                  },
                },
              ]
            : []),
        ] as Filter[],
        onResetAll: () => {
          onResetAll()
          resetPage()
        },
      }}
      resetSearchAndFilters={() => {
        onResetAll()
        resetPage()
      }}
      refetchResults={refetch}
      emptyState={{
        dataName: 'customers',
        description: t('customers.description'),
      }}
      pagination={{
        page,
        rowsPerPage,
        total: totalElements,
        onChangePage: handleChangePage,
        onChangeRowsPerPage: handleChangeRowsPerPage,
      }}
    >
      <ApplicationItems applications={applications} useV2Only />
    </ResultsLayout>
  )
}
